/* eslint-disable no-underscore-dangle */
import DialogWithClose from '@components/DialogWithClose';
import { Image, Video } from '@components/portable-text';
import { Button, Grid, MenuItem, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Check } from '@material-ui/icons';
import { PortableText } from '@portabletext/react';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getReferenceDate, readNotificationStorage, writeNotificationStorage } from './notification-helpers';
import { AppNotification, NotificationType } from './types';

const useStyles = makeStyles((theme) => ({
  menuItem: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    display: 'block',
  },
  title: {
    fontWeight: 'bold',
    width: '95%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  detailTitle: {
    fontWeight: 'bold',
    textDecoration: 'underline',
  },
  title_important: {
    textDecorationColor: theme.palette.error.main,
  },
  title_message: {
    textDecorationColor: theme.palette.info.main,
  },
  title_other: {
    textDecorationColor: theme.palette.secondary.main,
  },
  title_update: {
    textDecorationColor: theme.palette.primary.main,
  },
  shortInfo: {
    display: 'block',
    whiteSpace: 'pre-line',
  },
  border: {
    border: '1px solid',
    borderRadius: theme.shape.borderRadius,
  },
  border_important: { borderColor: theme.palette.error.main },
  border_message: {
    borderColor: theme.palette.info.main,
  },
  border_other: { borderColor: theme.palette.secondary.main },
  border_update: { borderColor: theme.palette.primary.main },
  time: {
    textAlign: 'end',
    color: theme.palette.secondary.light,
  },
  actions: {
    padding: theme.spacing(2),
    paddingTop: theme.spacing(0),
  },
  content: {
    maxHeight: '50vh',
    overflowY: 'auto',
  },
  menuBlock: {
    display: 'block',
    width: '100%',
  },
}));

type NotificationItemProps = {
  message: AppNotification;
  refetch: () => void;
  login: boolean;
};

const NotificationItem = ({ message, refetch, login }: NotificationItemProps) => {
  const [isOpen, setIsOpen] = useState(
    (!message.isRead && message.messageType === NotificationType.Important) || (!message.isRead && login),
  );
  const className = useStyles();
  const refDate = getReferenceDate(message);
  const addedToday =
    refDate instanceof Date && !Number.isNaN(refDate.getTime())
      ? refDate.toDateString() === new Date().toDateString()
      : false;
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const handleRead = () => {
    setIsOpen(false);
    const newItem = { id: message._id, date: new Date().toISOString() };
    writeNotificationStorage([...readNotificationStorage().filter((item) => item.id !== message._id), newItem]);
    refetch();
  };

  return (
    <>
      <MenuItem
        onClick={() => setIsOpen(true)}
        className={clsx(className.menuItem, className.border, className[`border_${message.messageType}`])}
        component="div"
      >
        <div className={className.menuBlock}>
          <Typography className={className.title}>{message.title}</Typography>
          <Typography variant="caption" className={className.shortInfo}>
            {message.shortInfo}
          </Typography>
          <Typography
            variant="caption"
            className={className.time}
            component="div"
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            {message.isRead && <Check fontSize="small" style={{ color: 'green', marginRight: '8px' }} />}
            <span>
              {refDate &&
                !Number.isNaN(refDate.getTime()) &&
                (addedToday
                  ? refDate.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' })
                  : refDate.toLocaleDateString())}
            </span>
          </Typography>
        </div>
      </MenuItem>
      <DialogWithClose
        maxWidth="sm"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        titleClassName={clsx(className.detailTitle, className[`title_${message.messageType}`])}
        actionsBottom={
          <Grid container justifyContent="space-between" alignItems="flex-end" className={className.actions}>
            <Grid item>
              <Typography variant="caption">
                {t('notificationBanner.posted')}:{' '}
                {refDate && !Number.isNaN(refDate.getTime())
                  ? `${refDate.toLocaleDateString(language)} ${t('timer.at')} ${refDate.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' })}`
                  : '-'}{' '}
                {language === 'de' ? 'Uhr' : ''}
              </Typography>
            </Grid>
            <Grid item>
              <Button
                color="primary"
                variant="contained"
                disabled={message.isRead}
                onClick={() => {
                  setIsOpen(false);
                  handleRead();
                }}
              >
                {t('notificationBanner.markAsRead')}
              </Button>
            </Grid>
          </Grid>
        }
        title={message.title}
      >
        <div className={className.content}>
          <Typography component="div">
            <PortableText
              value={message.description}
              components={{
                types: {
                  image: Image,
                  video: Video,
                },
              }}
            />
          </Typography>
        </div>
      </DialogWithClose>
    </>
  );
};

export default NotificationItem;
